import React, { useRef, useEffect, useState } from "react";
import { Container, Row, Col, Card, ListGroup } from "react-bootstrap";
import { useTranslation } from "react-i18next"; // Import translations
import "../App.css";
import { log } from "console";

const Menu: React.FC = () => {
  const { t } = useTranslation(); // Hook for translations

  // Dynamically fetch menu items from translations
  const menuItems = t("menuItems", { returnObjects: true }) as Record<
    string,
    { name: string; price: string; description: string }[]
  >;
  // References for each category section
  const categoryRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const mobileCategoriesRef = useRef<HTMLDivElement | null>(null);

  // State for sticky menu and active category
  const [sticky, setSticky] = useState(false);
  const [activeCategory, setActiveCategory] = useState(Object.keys(menuItems)[0]);
  const [scrolling, setScrolling] = useState(false); // State to control scrolling behavior

  // Effect to observe category sections and update active category
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (scrolling) return; // Ignore updates during scrolling

        // Find visible sections and sort them by position
        const visibleSections = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visibleSections.length > 0) {
          const visibleCategory = visibleSections[0].target.getAttribute("data-category");
          if (visibleCategory) {
            setActiveCategory(visibleCategory);
            // Scroll the horizontal menu bar to keep the active category in view
            if (mobileCategoriesRef.current) {
              const container = mobileCategoriesRef.current;
              const activeElement = container.querySelector(`[data-category="${visibleCategory}"]`);
              if (activeElement) {
                const offsetLeft = (activeElement as HTMLElement).offsetLeft;
                container.scrollTo({ left: offsetLeft - 10, behavior: "smooth" });

              }
            }
          }
        }
      },
      {
        root: null,
        rootMargin: "-200px 0px -60% 0px", // Adjust margins for better visibility
        threshold: 0.1, // Trigger when 10% of the section is visible
      }
    );

    // Observe each category section
    Object.keys(categoryRefs.current).forEach((category) => {
      const section = categoryRefs.current[category];
      if (section) {
        observer.observe(section);
      }
    });

    // Handle sticky menu behavior on scroll
    const handleScroll = () => {
      if (!mobileCategoriesRef.current) return;
      const { top } = mobileCategoriesRef.current.getBoundingClientRect();
      setSticky(top <= 150); // Make menu sticky when it reaches the top
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      observer.disconnect(); // Clean up observer
    };
  }, [scrolling]);

  // Function to scroll to a specific category
  const scrollToCategory = (category: string) => {
    const categoryElement = categoryRefs.current[category];
    if (categoryElement) {
      setScrolling(true); // Set scrolling state to true
      const yOffset = -220; // Offset to account for sticky header
      const y =
        categoryElement.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });

      // Reset scrolling state after animation
      setTimeout(() => {
        setScrolling(false);
      }, 700); // Animation duration (700ms)
    }
  };

  return (
    <Container className="container-fluid" style={{ paddingTop: "140px" }}>
      <Row>
        {/* Sidebar menu for larger screens */}
        <Col md={3} className="d-none d-md-block">
          <div
            ref={mobileCategoriesRef}
            className={`category-container ${sticky ? "sticky-category" : ""}`}
            style={{
              top: sticky ? "150px" : "",
              overflowY: "auto",
              maxHeight: "80vh",
              maxWidth: "auto",
            }}
          >
            <Card className="shadow-sm border-0">
              <Card.Body>
                <h5
                  className="text-center"
                  style={{ color: "#F58220", fontWeight: "bold" }}
                >
                  {t("menu")}
                </h5>
                <ListGroup variant="flush">
                  {Object.keys(menuItems).map((category, index) => (
                    <ListGroup.Item
                      key={index}
                      action
                      onClick={() => scrollToCategory(category)}
                      style={{
                        cursor: "pointer",
                        fontWeight: "500",
                        color: "#4F4F4F",
                        padding: "10px",
                        transition: "background-color 0.3s",
                      }}
                    >
                      {t(`menuCategories.${category}`)}
                    </ListGroup.Item>
                  ))}
                </ListGroup>
              </Card.Body>
            </Card>
          </div>
        </Col>

        {/* Horizontal menu for mobile devices */}
        <Col xs={12} className="d-md-none text-center mb-3 ">
          <div
            ref={mobileCategoriesRef}
            style={{
              position: "fixed",
              top: 170,
              left: 0,
              right: 0,
              zIndex: 1000,
              backgroundColor: "#fff",
              borderBottom: "1px solid #ddd",
              overflowX: "auto",
              whiteSpace: "nowrap",
              padding: "10px 0",
            }}
          >
            {Object.keys(menuItems).map((category, index) => (
              <span
                key={index}
                onClick={() => {
                  scrollToCategory(category);
                  setActiveCategory(category);
                }}
                style={{
                  display: "inline-block",
                  padding: "10px 16px",
                  color: "#F58220",
                  fontWeight: "bold",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  borderBottom: category === activeCategory ? '3px solid #F58220' : '3px solid transparent',
                  transition: "border-bottom 0.3s",
                }}
                data-category={category}
              >
                {t(`menuCategories.${category}`)}
              </span>
            ))}
          </div>
        </Col>

        {/* Menu content */}
        <Col md={9}>
          <Card className="shadow-sm border-0 menu">
            <Card.Body>
              <Row>
                {Object.keys(menuItems).map((category, index) => (
                  <Col
                    md={12}
                    ref={(el: HTMLDivElement | null) =>
                      (categoryRefs.current[category] = el)
                    }
                    data-category={category}
                    style={{
                      marginBottom: "3rem",
                    }}
                    className="category-section"
                  >
                    <p
                      className="fw-bold fs-1 category-title"
                      style={{ fontFamily: "Yeseva One, cursive" }}
                    >
                      {t(`menuCategories.${category}`)}
                    </p>
                    <ul className="list-unstyled">
                      {menuItems[category].map((item, itemIndex) => (
                        <li key={itemIndex} className="d-flex flex-column mb-3">
                          <span className="fw-bold">
                            {item.name} - {item.price} zł
                          </span>
                          <p className="text-muted" style={{ margin: 0 }}>
                            {item.description}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </Col>
                ))}
              </Row>
            </Card.Body>
          </Card>
          <div style={{ marginTop: 20 }}>
            <p>{t("alergens")}</p>
            <p>{t("menuDifferences")}</p>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default Menu;
